import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { BASELINE, MMTLOGIN } from "../util/index";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import EyeTrackingHeader from "./eyetracker";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import Policy from '../Components/Policy';

const SignInForm = ({ onLogin }) => {
    const { savelogin, user } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Policy 관련 상태
    const [showPolicy, setShowPolicy] = useState(false);
    const [tempUserData, setTempUserData] = useState(null);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    // 자동 로그인 로직: localStorage에 저장된 크리덴셜을 감지해서 로그인 시도 후 삭제
    useEffect(() => {
        const storedEmail = localStorage.getItem("quiz-email");
        const storedPassword = localStorage.getItem("quiz-password");
        if (storedEmail && storedPassword) {
            axios.post(BASELINE + 'user/login', { email: storedEmail, password: storedPassword })
                .then(response => {
                    if (response.data.u_id !== '') {
                        // 만약 agreement_log이 없는 사용자라면, 자동 로그인 후 약관 동의 화면을 보여줄 수도 있음.
                        if (!response.data.agreement_log) {
                            setTempUserData(response.data);
                            setShowPolicy(true);
                        } else {
                            savelogin(response.data);
                            onLogin();
                            if (location.state?.from?.pathname) {
                                navigate(location.state.from.pathname);
                            } else {
                                navigate("/main");
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error("Auto-login failed:", error);
                })
                .finally(() => {
                    // 자동 로그인 시도 후, 크리덴셜은 삭제하여 재사용되지 않도록 함
                    localStorage.removeItem("quiz-email");
                    localStorage.removeItem("quiz-password");
                });
        }
    }, [savelogin, onLogin, navigate, location.state]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        axios.post(BASELINE + 'user/login', formData)
            .then(function (response) {
                if (response.data.u_id !== '') {
                    if (!response.data.agreement_log) {
                        setTempUserData(response.data);
                        setShowPolicy(true);
                    } else {
                        savelogin(response.data);
                        setFormData({ email: '', password: '' });
                        onLogin();
                        if (location.state?.from?.pathname) {
                            navigate(location.state.from.pathname);
                        } else {
                            navigate("/main");
                        }
                    }
                } else {
                    window.alert("Please double check the login information.");
                }
            })
            .catch(function (error) {
                window.alert("Please double check the login information.");
            });
    };

    const mmt_login = () => {
        axios.post(MMTLOGIN + 'mmtlogin', formData)
            .then(function (response) {
                if (response.data.hasOwnProperty('uid') && response.data.uid !== '' && response.data.status !== "Registered") {
                    if (!response.data.agreement_log) {
                        setTempUserData(response.data);
                        setShowPolicy(true);
                    } else {
                        savelogin(response.data);
                        setFormData({ email: '', password: '' });
                        onLogin();
                        navigate("/main");
                    }
                } else {
                    window.alert("Please double check the login information. or Contact to the administer to activate the account.");
                }
            })
            .catch(function (error) {
                window.alert("Please double check the login information. or Contact to the administer to activate the account.");
            });
    };

    // Google 로그인 성공/실패 처리
    const handleGoogleFailure = (error) => {
        alert('Google Sign-In was unsuccessful. Please try again.');
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        const token = credentialResponse.credential;
        const decoded = jwtDecode(token);
        const { given_name, family_name, email } = decoded;

        try {
            const dataToSend = {
                first_name: given_name,
                last_name: family_name,
                email,
            };

            axios.post(BASELINE + 'user/login/google', dataToSend)
                .then(function (response) {
                    if (response.data.u_id !== '') {
                        if (!response.data.agreement_log) {
                            setTempUserData(response.data);
                            setShowPolicy(true);
                        } else {
                            savelogin(response.data);
                            onLogin();
                            if (location.state?.from?.pathname) {
                                navigate(location.state.from.pathname);
                            } else {
                                navigate("/main");
                            }
                        }
                    } else {
                        window.alert("Please signup before login.");
                        navigate("/signup");
                    }
                })
                .catch(function (error) {
                    window.alert("Please signup before login.");
                    navigate("/signup");
                });
        } catch (error) {
            alert('An error occurred during Google Sign-In registration. Please try again later.');
        }
    };

    // Policy가 { policy_1, policy_2, policy_3 } 를 한 번에 전달
    const handlePolicyAgree = (policyData) => {
        const updatedUser = { ...tempUserData, agreement_log: policyData };
        axios.post(BASELINE + 'user/update/agreement', {
            u_id: updatedUser.u_id,
            agreement_log: JSON.stringify(policyData)
        })
            .then(() => {
                savelogin(updatedUser);
                onLogin();
                navigate("/main");
            })
            .catch(() => {
                alert("Failed to update agreement log. Please try again.");
            });
    };

    if (showPolicy) {
        return <Policy onAgree={handlePolicyAgree} />;
    }

    return (
        <div className="flex flex-col justify-center items-center min-h-screen bg-gray-100 px-4">
            <div className="w-full max-w-md border bg-white p-6 sm:p-8 lg:p-10 rounded-md shadow-md text-[#232323]">
                <EyeTrackingHeader />

                <p className="mb-4 text-gray-500 text-[15px] text-center">
                    Login in to P-vault.com
                </p>

                <form onSubmit={handleSubmit}>
                    <input
                        placeholder="Username or email address*"
                        className="mb-4 w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                    />

                    <div className="relative">
                        <input
                            placeholder="Password*"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                        />
                        <span
                            className="absolute right-3 top-2.5 text-gray-400 cursor-pointer"
                            onClick={togglePasswordVisibility}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.478 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        </span>
                    </div>

                    <div className="mt-2 text-right">
                        <a
                            href="/dashboard/reset/passwrod"
                            className="font-bold text-sm text-purple-600 hover:underline"
                        >
                            Forgot password?
                        </a>
                    </div>

                    <button
                        type="submit"
                        className="w-full mt-5 py-2 text-white bg-teal-500 rounded hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors"
                    >
                        Continue
                    </button>

                    <div className='mt-2 mb-4 text-[15px]'>
                        Don't have an account?
                        <Link to="/signup" className="ml-[3px] text-purple-600 underline mb-2 sm:mb-0">
                            Sign Up
                        </Link>
                    </div>

                    <div className="relative flex items-center my-4">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="mx-2 text-gray-400 text-sm">OR</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                    </div>

                    <button
                        className="rounded w-full border text-slate-900 py-2 px-4"
                        type="button"
                        onClick={mmt_login}
                    >
                        Continue with MMT Prep
                    </button>

                    {/* Google 로그인 임시 비활성화
                    <div className="mt-4">
                        <GoogleOAuthProvider clientId="208805948909-68s39ohls7juea9hmou6l11sbpvt0pms.apps.googleusercontent.com">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={handleGoogleFailure}
                                useOneTap
                                text="continue_with"
                                shape="pill"
                            />
                        </GoogleOAuthProvider>
                    </div>
                    */}
                </form>
            </div>
        </div>
    );
};

export default SignInForm;
